'use client';

import { useMotionValueEvent, type MotionValue } from 'motion/react';
import { memo, useRef } from 'react';
import { useSkyShell } from '../../skyShellContext';
import { fieldOfViewRange } from './fieldOfViewRange';
import styles from './FieldOfView.module.css';

interface FieldOfViewProps {
  bearing: MotionValue<number>;
  ticksPerTurn: number;
  tickSpacing: number;
}

export const FieldOfView = memo(function FieldOfView({
  bearing,
  ticksPerTurn,
  tickSpacing,
}: FieldOfViewProps) {
  const { cameraFrameRef, subscribeCamera } = useSkyShell();
  const ref = useRef<HTMLDivElement>(null);
  const previousRange = useRef({ start: Infinity, end: Infinity });

  const updateRange = () => {
    const camera = cameraFrameRef.current;
    const element = ref.current;
    if (!camera || !element) return;

    const range = fieldOfViewRange(
      camera,
      bearing.get(),
      ticksPerTurn,
      window.innerWidth / window.innerHeight
    );
    const minimumChange = 0.25 / tickSpacing;
    if (
      Math.abs(range.start - previousRange.current.start) < minimumChange &&
      Math.abs(range.end - previousRange.current.end) < minimumChange
    )
      return;

    element.style.setProperty('--view-start', String(range.start));
    element.style.setProperty('--view-end', String(range.end));
    element.style.visibility = 'visible';
    previousRange.current = range;
  };

  useMotionValueEvent(bearing, 'change', updateRange);

  const attach = (element: HTMLDivElement | null) => {
    ref.current = element;

    if (!element) return;

    const unsubscribe = subscribeCamera(updateRange);
    window.addEventListener('resize', updateRange);
    updateRange();

    return () => {
      unsubscribe();
      window.removeEventListener('resize', updateRange);
      ref.current = null;
    };
  };

  return (
    <div ref={attach} className={styles.fieldOfView} aria-hidden={true}>
      <div className={styles.viewArc} />
    </div>
  );
});
